import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { FaWhatsapp } from 'react-icons/fa';
import Image from '../../../components/AppImage';

const BlogPostDetail = ({ post, handleBackToBlog }) => {
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  if (!post) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-text-primary">Post Not Found</h2>
        <button onClick={handleBackToBlog} className="mt-4 btn-primary">
          <Icon name="ArrowLeft" size={20} className="mr-2" />
          Back to Blog
        </button>
      </div>
    );
  }

  const currentUrl = window.location.href; 

  const renderEditorBlock = (block) => {
    if (!block) return null;

    switch (block.type) {
      case 'paragraph':
        return <p key={block.id} className="mb-4">{block.data.text}</p>;

      case 'list':
        if (block.data.style === 'ordered') {
          return <ol key={block.id} className="list-decimal pl-6 mb-4">
            {block.data.items.map((item, i) => <li key={i}>{item.content}</li>)}
          </ol>;
        } else if (block.data.style === 'checklist') {
          return <ul key={block.id} className="list-disc pl-6 mb-4">
            {block.data.items.map((item, i) => (
              <li key={i}>
                <input type="checkbox" checked={item.meta?.checked || false} readOnly className="mr-2" />
                {item.content}
              </li>
            ))}
          </ul>;
        } else {
          return <ul key={block.id} className="list-disc pl-6 mb-4">
            {block.data.items.map((item, i) => <li key={i}>{item.content}</li>)}
          </ul>;
        }

      case 'code':
        return <pre key={block.id} className="bg-black text-green-400 p-4 rounded mb-4 overflow-x-auto">
          <code>{block.data.code}</code>
        </pre>;

      case 'warning':
        return <div key={block.id} className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
          <strong>{block.data.title}</strong>
          <p>{block.data.message}</p>
        </div>;

      case 'raw':
        return <div key={block.id} className="mb-4" dangerouslySetInnerHTML={{ __html: block.data.html }} />;

      case 'layout':
        return block.data.layout.map((col, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {block.data.content[idx]?.blocks?.map((b) => renderEditorBlock(b))}
          </div>
        ));

      default:
        return null;
    }
  };

  const handleShare = (platform) => {
    const encodedTitle = encodeURIComponent(post.title);
    const encodedUrl = encodeURIComponent(currentUrl);

    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodedTitle}%20-%20${encodedUrl}`, '_blank');
    if (platform === 'email') window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
    if (platform === 'copy') {
      navigator.clipboard.writeText(currentUrl);
      alert('Link copied to clipboard!');
    }
  };

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (comment && name && email) {
      console.log({ name, email, comment, postId: post.id });
      setComment(''); setName(''); setEmail('');
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <button onClick={handleBackToBlog} className="flex items-center text-primary hover:text-primary-700 font-medium mb-8">
          <Icon name="ArrowLeft" size={20} className="mr-2" />
          Back to All Articles
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-9">
            <article className="bg-surface rounded-xl overflow-hidden shadow-2xl">
              <div className="relative h-[450px] w-full overflow-hidden">
                <Image src={post.coverImage?.url || post.authorImage?.url} alt={post.title} className="w-full h-full object-cover" />
                <div className="absolute bottom-6 left-6">
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-primary text-white shadow-lg">
                    {post.category?.name}
                  </span>
                </div>
              </div>

              <div className="p-8 md:p-12">
                <h1 className="text-3xl md:text-4xl font-heading font-extrabold text-text-primary mb-6">{post.title}</h1>
                <div className="flex flex-wrap items-center space-x-6 text-sm text-text-secondary mb-8 pb-4 border-b border-border">
                  <div className="flex items-center">
                    <Icon name="Calendar" size={16} className="mr-2 text-primary" />
                    <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Clock" size={16} className="mr-2 text-primary" />
                    <span>{post.readTime} min</span>
                  </div>
                </div>

                <div className="prose prose-lg max-w-none text-text-primary">
                  {post.content?.blocks?.map((block) => renderEditorBlock(block))}
                </div>

                <div className="flex flex-wrap gap-3 mt-12 pt-6 border-t border-border">
                  <span className="text-base font-semibold text-text-primary mr-2">Topics:</span>
                  {post.tags?.map((tag) => (
                    <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">#{tag}</span>
                  ))}
                </div>
              </div>
            </article>

            <div className="mt-10 p-8 bg-surface border border-border rounded-xl flex items-start space-x-6 shadow-md">
              <div className="w-20 h-20 rounded-full overflow-hidden">
                <Image src={post.authorImage?.url} alt={post.authorName} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-secondary">Author</p>
                <h3 className="text-2xl font-semibold text-text-primary mb-1">{post.authorName}</h3>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-8 mt-10 lg:mt-0">
            <div className="bg-surface p-6 rounded-xl shadow-lg border border-border">
              <h3 className="text-lg font-heading font-semibold mb-4">Share</h3>
              <div className="flex space-x-3">
                <button onClick={() => handleShare('whatsapp')} className="p-3 bg-green-500 text-white rounded-full"><FaWhatsapp size={20} /></button>
                <button onClick={() => handleShare('email')} className="p-3 bg-red-500 text-white rounded-full"><Icon name="Mail" size={20} /></button>
                <button onClick={() => handleShare('copy')} className="p-3 bg-gray-500 text-white rounded-full"><Icon name="Link" size={20} /></button>
              </div>
            </div>

            <div className="bg-surface p-6 rounded-xl shadow-lg border">
              <h3 className="text-lg font-bold mb-4">Send Feedback</h3>
              <form onSubmit={handleSubmitComment} className="space-y-4">
                <input type="text" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} className="w-full p-2 border rounded" />
                <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full p-2 border rounded" />
                <textarea placeholder="Your comment" value={comment} onChange={e => setComment(e.target.value)} rows="4" className="w-full p-2 border rounded" />
                <button type="submit" className="w-full bg-primary text-white py-2 rounded">Send</button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostDetail;
